import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useApi, useAuthApi } from "../hooks/api/axiosInterceptor";

const CalendarWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const CalendarContainer = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CalendarSection = styled.div`
  flex: 1;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const MonthYearDisplay = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const NavButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #2056ad;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #1a4786;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 0.5rem;
`;

const WeekDayLabel = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 0.5rem;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid #ddd;
`;

const DayCell = styled.div`
  min-height: 80px;
  border: 1px solid #ddd;
  padding: 0.5rem;
  background-color: ${props =>
    props.isToday ? '#e8f0fe' :
        props.isSelected ? '#d3e4fd' :
            props.isOtherMonth ? '#f5f5f5' : 'white'};
  cursor: pointer;
  position: relative;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#d3e4fd' : '#f8f9fa'};
  }
`;

const DayNumber = styled.div`
  font-weight: ${props => props.isToday ? 'bold' : 'normal'};
  color: ${props =>
    props.isOtherMonth ? '#999' :
        props.isToday ? '#2056ad' : 'black'};
  margin-bottom: 0.25rem;
`;

const EventText = styled.div`
  font-size: 0.75rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const EventInputSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  background-color: #f8f9fa;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const EventInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1.1rem;
  
  &:focus {
    outline: none;
    border-color: #2056ad;
    box-shadow: 0 0 0 2px rgba(32, 86, 173, 0.2);
  }
`;

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
`;

const AddButton = styled.button`
  padding: 0.5rem 1.5rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
  
  &:hover {
    background-color: #218838;
  }
`;

const EventList = styled.div`
  flex: 0 0 300px;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const EventListHeader = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: bold;
`;

const EventListContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
`;

const EventItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  background-color: ${props => props.isSelected ? '#e9ecef' : 'transparent'};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #e9ecef;
  }
`;

const Checkbox = styled.input`
  margin-right: 1rem;
  cursor: pointer;
`;

const EventContent = styled.span`
  flex: 1;
`;

const DeleteButton = styled.button`
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #c82333;
  }
  
  &:disabled {
    background-color: #dee2e6;
    cursor: not-allowed;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6c757d;
  padding: 1rem 0;
`;

const CalendarPage = () => {

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState({});
    const [newEvent, setNewEvent] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [selectedEvents, setSelectedEvents] = useState([]);

    useEffect(() => {
        const today = new Date();
        setEventDate(formatDate(today));
    }, []);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const res = await useApi.get('/events');
                const data = res.data;
                if (data.status === 'SUCCESS') {
                    const grouped = {};
                    data.data.forEach(ev => {
                        if (!grouped[ev.event_date]) grouped[ev.event_date] = [];
                        grouped[ev.event_date].push(ev.content);
                    });
                    setEvents(grouped);
                }
            } catch (e) {
                console.error('일정 불러오기 실패:', e);
            }
        };

        fetchEvents();
    }, []);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handlePrevMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
    };

    const handleDayClick = (day, month, year) => {
        const newSelectedDate = new Date(year, month, day);
        setSelectedDate(newSelectedDate);
        setEventDate(formatDate(newSelectedDate));
        setSelectedEvents([]);
    };

    const handleAddEvent = async () => {
        if (!newEvent || !eventDate) return;
        try {
            const res = await useAuthApi.post('/events', {
                event_date: eventDate,
                content: newEvent
            });

            const data = res.data;
            if (data.status === 'SUCCESS') {
                setEvents(prev => ({
                    ...prev,
                    [eventDate]: [...(prev[eventDate] || []), newEvent]
                }));
                setNewEvent('');
            } else {
                console.error(data.message);
            }
        } catch (e) {
            console.error('일정 추가 실패:', e);
        }
    };

    const handleEventSelect = (index) => {
        setSelectedEvents(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleDeleteSelected = async () => {
        const dateKey = formatDate(selectedDate);
        const selectedContents = (events[dateKey] || []).filter((_, i) => selectedEvents.includes(i));

        try {
            for (const content of selectedContents) {
                await useAuthApi.delete('/events', {
                    data: {
                        event_date: dateKey,
                        content,
                    },
                });
            }

            // 클라이언트 상태에서도 삭제
            setEvents(prev => {
                const newEvents = { ...prev };
                if (newEvents[dateKey]) {
                    newEvents[dateKey] = newEvents[dateKey].filter((_, i) => !selectedEvents.includes(i));
                    if (newEvents[dateKey].length === 0) {
                        delete newEvents[dateKey];
                    }
                }
                return newEvents;
            });
            setSelectedEvents([]);
        } catch (error) {
            console.error('일정 삭제 실패:', error);
            alert('일정 삭제에 실패했습니다.');
        }
    };

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const today = new Date();

        const days = [];
        const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

        const prevMonthDays = getDaysInMonth(year, month - 1);
        for (let i = firstDay - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const date = new Date(year, month - 1, day);
            const dateKey = formatDate(date);
            days.push(
                <DayCell
                    key={`prev-${day}`}
                    isOtherMonth
                    onClick={() => {
                        setCurrentDate(new Date(year, month - 1));
                        handleDayClick(day, month - 1, year);
                    }}
                >
                    <DayNumber isOtherMonth>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx}>{event.slice(0, 6)}</EventText>
                    ))}
                </DayCell>
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateKey = formatDate(date);
            const isSelectedDay =
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === month &&
                selectedDate.getFullYear() === year;

            const isToday =
                today.getDate() === day &&
                today.getMonth() === month &&
                today.getFullYear() === year;

            days.push(
                <DayCell
                    key={day}
                    isToday={isToday}
                    isSelected={isSelectedDay}
                    onClick={() => handleDayClick(day, month, year)}
                >
                    <DayNumber isToday={isToday}>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx}>{event.slice(0, 6)}</EventText>
                    ))}
                </DayCell>
            );
        }

        const remainingCells = 42 - days.length;
        for (let day = 1; day <= remainingCells; day++) {
            const date = new Date(year, month + 1, day);
            const dateKey = formatDate(date);
            days.push(
                <DayCell
                    key={`next-${day}`}
                    isOtherMonth
                    onClick={() => {
                        setCurrentDate(new Date(year, month + 1));
                        handleDayClick(day, month + 1, year);
                    }}
                >
                    <DayNumber isOtherMonth>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx}>{event.slice(0, 6)}</EventText>
                    ))}
                </DayCell>
            );
        }

        return (
            <>
                <WeekDays>
                    {weekDays.map(day => (
                        <WeekDayLabel key={day}>{day}</WeekDayLabel>
                    ))}
                </WeekDays>
                <CalendarGrid>{days}</CalendarGrid>
            </>
        );
    };

    const getEventsByDate = (date) => {
        const dateKey = formatDate(date);
        return events[dateKey] || [];
    };

    return (
        <CalendarWrapper>
            <CalendarContainer>
                <CalendarSection>
                    <CalendarHeader>
                        <MonthYearDisplay>
                            {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                        </MonthYearDisplay>
                        <NavigationButtons>
                            <NavButton onClick={handlePrevMonth}>이전</NavButton>
                            <NavButton onClick={handleNextMonth}>다음</NavButton>
                        </NavigationButtons>
                    </CalendarHeader>
                    {renderCalendar()}
                </CalendarSection>

                <EventList>
                    <EventListHeader>
                        {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 일정
                    </EventListHeader>
                    <EventListContainer>
                        {getEventsByDate(selectedDate).length > 0 ? (
                            <>
                                {getEventsByDate(selectedDate).map((event, index) => (
                                    <EventItem
                                        key={index}
                                        isSelected={selectedEvents.includes(index)}
                                        onClick={() => handleEventSelect(index)}
                                    >
                                        <Checkbox
                                            type="checkbox"
                                            checked={selectedEvents.includes(index)}
                                            onChange={() => handleEventSelect(index)}
                                        />
                                        <EventContent>{event}</EventContent>
                                    </EventItem>
                                ))}
                                <DeleteButton
                                    onClick={handleDeleteSelected}
                                    disabled={selectedEvents.length === 0}
                                >
                                    선택한 일정 삭제
                                </DeleteButton>
                            </>
                        ) : (
                            <EmptyMessage>일정이 없습니다.</EmptyMessage>
                        )}
                    </EventListContainer>
                </EventList>
            </CalendarContainer>

            <EventInputSection>
                <h3>일정 추가</h3>
                <InputWrapper>
                    <EventInput
                        type="text"
                        placeholder="일정을 입력하세요"
                        value={newEvent}
                        onChange={(e) => setNewEvent(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddEvent()}
                    />
                    <InputGroup>
                        <DateInput
                            type="date"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                        />
                        <AddButton onClick={handleAddEvent}>추가</AddButton>
                    </InputGroup>
                </InputWrapper>
            </EventInputSection>
        </CalendarWrapper>
    );
};

export default CalendarPage;
